import { useEffect, useLayoutEffect, useRef } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Outlet, useHref, useLocation } from 'react-router';
import { createGlobalStyle } from 'styled-components';

import { TwitchPlayer } from '@sorare/core/src/components/TwitchPlayer';
import { SidetabHandler as TypeformSidetabHandler } from '@sorare/core/src/components/typeform/SidetabHandler';
import { IS_PRERENDER } from '@sorare/core/src/config';
import { StyleSheetManager } from '@sorare/core/src/contexts/StyleSheetManager';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { HrefLang } from '@sorare/core/src/contexts/intl/Provider';
import {
  useFeatureFlags,
  useLDClient,
} from '@sorare/core/src/hooks/useFeatureFlags';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { Maintenance } from '@sorare/core/src/routing/Maintenance';

import { AppProviders } from './AppProviders.tsx';
import { LazyBlockchainProviders } from './components/BlockChainProviders/lazy.tsx';
import { MobileAppInit } from './components/MobileAppInit/index.tsx';

function getOrCreateMeta(name: string): HTMLMetaElement {
  let meta = document.querySelector<HTMLMetaElement>(`meta[name=${name}]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  return meta;
}

const PrerenderMeta = () => {
  // oxlint-disable-next-line sorare/no-window-location
  const initial = useRef(window.location.pathname);
  const location = useLocation();

  const href = useHref(location.pathname);

  useEffect(() => {
    if (href !== initial.current) {
      // https://docs.prerender.io/docs/11-best-practices
      const statusMeta = getOrCreateMeta('prerender-status-code');
      statusMeta.content = '301';
      const locationMeta = getOrCreateMeta('prerender-header');
      locationMeta.content = `Location: ${href}`;
    }
  }, [href]);
  return null;
};

export const InnerAppLayout = () => {
  const { currentUser } = useCurrentUserContext();

  if (currentUser) {
    return (
      <LazyBlockchainProviders>
        <TypeformSidetabHandler />
        <Outlet />
      </LazyBlockchainProviders>
    );
  }
  return <Outlet />;
};

const GlobalBackground = createGlobalStyle`
  body {
    color: var(--c-white);
    background: var(--c-black);
  }
  `;

const LightMode = () => {
  const { lifecycle } = useLifecycle();
  const lightMode = lifecycle?.[LIFECYCLE.experimentalLightMode];
  useLayoutEffect(() => {
    if (lightMode) {
      document.documentElement.classList.add('light-mode');
    }
    return () => document.documentElement.classList.remove('light-mode');
  }, [lightMode]);

  return null;
};

export const OuterAppLayout = () => {
  const client = useLDClient();
  const {
    flags: { useMaintenance },
  } = useFeatureFlags();

  if (!client) {
    return null;
  }

  if (useMaintenance?.maintenance) {
    return (
      <Maintenance
        startDate={new Date(useMaintenance.start_date)}
        endDate={new Date(useMaintenance.end_date)}
      />
    );
  }

  return (
    <HelmetProvider>
      <GlobalBackground />
      <MobileAppInit />
      <HrefLang />
      {!!IS_PRERENDER && <PrerenderMeta />}
      <StyleSheetManager>
        <AppProviders>
          <LightMode />
          <Outlet />
          {!IS_PRERENDER && <TwitchPlayer />}
        </AppProviders>
      </StyleSheetManager>
    </HelmetProvider>
  );
};
